/**
 * ============================================================================
 * ProgramPageConnectModalForm — Model (form values + RHF resolver)
 * ============================================================================
 * Explicit resolver replaces manual errors state so controlled
 * setValue/watch inputs populate formState.errors on empty submit.
 * No Yup — resolver matches pre-MVC checkAllFieldsValid / regexForInputs.
 * Program-page form: name / phone / email / terms only (ProgramId prop-driven).
 * ============================================================================
 */

import type { FieldErrors, Resolver } from "react-hook-form";

export interface ProgramPageConnectModalFormValues {
  fullName: string;
  countryCode: string;
  phoneNumber: string;
  emailId: string;
  programId?: string;
  isTermsAccepted: boolean;
}

export const NATIONAL_CODE = "+91";

export const errorAccordingToFieldName: Record<string, string> = {
  fullName: "Please enter valid full name",
  countryCode: "Please enter valid country code",
  phoneNumber: "Please enter valid phone number",
  emailId: "Please enter valid email address",
  isTermsAccepted: "Please accept terms & conditions",
};

export const regexForInputs: Record<string, RegExp> = {
  fullName: /^[a-zA-Z][a-zA-Z ]{2,49}$/,
  countryCode: /^\+\d{1,3}(?:-\d{1,4})?$/,
  phoneNumberIndia: /^[1-9][0-9]{9}$/,
  phoneNumberIntl: /^[1-9][0-9]{6,14}$/,
  emailId:
    /^(?!.*\.\.)[a-zA-Z0-9](\.?[a-zA-Z0-9_-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/,
};

/**
 * Explicit RHF resolver — controlled inputs via setValue/watch.
 * Matches pre-MVC ProgramPageConnectModalForm validation semantics.
 */
export function createProgramPageConnectModalFormResolver(): Resolver<ProgramPageConnectModalFormValues> {
  return (values) => {
    const errors: FieldErrors<ProgramPageConnectModalFormValues> = {};

    if (!values.fullName || !regexForInputs.fullName.test(values.fullName)) {
      errors.fullName = {
        type: "validate",
        message: errorAccordingToFieldName.fullName,
      };
    }

    if (
      !values.countryCode ||
      !regexForInputs.countryCode.test(values.countryCode)
    ) {
      errors.countryCode = {
        type: "validate",
        message: errorAccordingToFieldName.countryCode,
      };
    }

    const phoneRegex =
      values.countryCode !== NATIONAL_CODE
        ? regexForInputs.phoneNumberIntl
        : regexForInputs.phoneNumberIndia;
    if (!values.phoneNumber || !phoneRegex.test(String(values.phoneNumber))) {
      errors.phoneNumber = {
        type: "validate",
        message: errorAccordingToFieldName.phoneNumber,
      };
    }

    if (!values.emailId || !regexForInputs.emailId.test(values.emailId)) {
      errors.emailId = {
        type: "validate",
        message: errorAccordingToFieldName.emailId,
      };
    }

    if (values.isTermsAccepted !== true) {
      errors.isTermsAccepted = {
        type: "validate",
        message: errorAccordingToFieldName.isTermsAccepted,
      };
    }

    const hasErrors = Object.keys(errors).length > 0;
    return hasErrors
      ? { values: {} as Record<string, never>, errors }
      : { values, errors: {} };
  };
}
